export const ENDPOINTS = {
  organizations: '/api/organizations',
  organizationSections: {
    get: (organizationId: string) => `/api/organizations/${organizationId}/sections`,
  },
  auth: {
    alfaCallback: '/api/auth/callback/alfa',
    alfaUrl: '/api/auth/alfa-url',
    logout: '/api/auth/logout',
    loginPhone: '/api/auth/login/phone',
    totpSetup: '/api/auth/totp/setup',
    totpConfirmSetup: '/api/auth/totp/confirm-setup',
    passwordReset: '/api/auth/password/reset',
  },
  chartSessions: {
    get: (organizationId: string) => `/api/organizations/${organizationId}/assistantSessions`,
    create: (organizationId: string) => `/api/organizations/${organizationId}/assistantSessions`,
    sendMessage: (organizationId: string, sessionId: string) =>
      `/api/organizations/${organizationId}/assistantSessions/${sessionId}/messages`,
    sendMessageStream: (organizationId: string, sessionId: string) =>
      `/api/organizations/${organizationId}/assistantSessions/${sessionId}/messages/stream`,
    chartModels: '/api/llm_models',
    putRate: (organizationId: string, sessionId: string, messageId: string) =>
      `/api/organizations/${organizationId}/assistantSessions/${sessionId}/messages/${messageId}/rate`,
    sendFeedback: (organizationId: string, sessionId: string, messageId: string) =>
      `/api/organizations/${organizationId}/assistantSessions/${sessionId}/messages/${messageId}/feedback`,
    getSessionMessages: (organizationId: string, sessionId: string) =>
      `/api/organizations/${organizationId}/assistantSessions/${sessionId}/messages`,
    getSessionById: (organizationId: string, sessionId: string) =>
      `/api/organizations/${organizationId}/assistantSessions/${sessionId}`,
    updateSession: (organizationId: string, sessionId: string) =>
      `/api/organizations/${organizationId}/assistantSessions/${sessionId}`,
    getChartTips: (organizationId: string) => `/api/organizations/${organizationId}/assistantSessions/suggestions`,
  },
  sessions: {
    whoamiKratos: '/sessions/whoami',
    whoami: '/api/auth/sessions/whoami',
  },
  documents: {
    tree: (organizationId: string) => `/api/organizations/${organizationId}/documents/tree`,
    latest: (organizationId: string) => `/api/organizations/${organizationId}/documents`,
    search: (organizationId: string) => `/api/organizations/${organizationId}/documents:search`,
    documentTypes: (organizationId: string) => `/api/organizations/${organizationId}/document_types`,
    documentSourceTypes: (organizationId: string) => `/api/organizations/${organizationId}/document_source_types`,
    add: (organizationId: string) => `/api/organizations/${organizationId}/documents/upload`,
    delete: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}`,
    download: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/download`,
    shortSummary: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/short_summary`,
    aiSummary: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/summary`,
    properties: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/properties`,
    meetingNotesStream: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/meeting-notes/stream`,
    meetingNotesLatest: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/meeting-notes/latest`,
  },
  permissions: {
    get: (organizationId: string) => `/api/organizations/${organizationId}/permissions/bulk`,
  },
  analyze: {
    available: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/analysis_types`,
    organizationAvailable: (organizationId: string) => `/api/organizations/${organizationId}/documents/analysis_types`,
    execute: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/analysis`,
    executeStream: (organizationId: string, documentId: string) =>
      `/api/organizations/${organizationId}/documents/${documentId}/analysis/stream`,
    result: (organizationId: string, documentId: string, analysisResultId: number) =>
      `/api/organizations/${organizationId}/documents/${documentId}/analysis_results/${analysisResultId}`,
    organizationResult: (organizationId: string, analysisResultId: number) =>
      `/api/organizations/${organizationId}/documents/analysis_results/${analysisResultId}`,
  },
  boardQuestionSets: {
    generate: (organizationId: string) => `/api/organizations/${organizationId}/questions/generate/stream`,
  },
  folder: {
    add: (organizationId: string) => `/api/organizations/${organizationId}/folders`,
    update: (organizationId: string, folderId: string) => `/api/organizations/${organizationId}/folders/${folderId}`,
    delete: (organizationId: string, folderId: string) => `/api/organizations/${organizationId}/folders/${folderId}`,
  },
  events: {
    list: (organizationId: string) => `/api/organizations/${organizationId}/events`,
    add: (organizationId: string) => `/api/organizations/${organizationId}/events`,
    update: (organizationId: string, eventId: string) => `/api/organizations/${organizationId}/events/${eventId}`,
    delete: (organizationId: string, eventId: string) => `/api/organizations/${organizationId}/events/${eventId}`,
  },
  news: {
    list: (organizationId: string) => `/api/organizations/${organizationId}/news`,
    summary: (organizationId: string, newsId: string) => `/api/organizations/${organizationId}/news/${newsId}`,
    digests: (organizationId: string) => `/api/organizations/${organizationId}/news/digests`,
    searchNews: (organizationId: string) => `/api/organizations/${organizationId}/news:search`,
    digestNews: (organizationId: string, digestId: string) =>
      `/api/organizations/${organizationId}/news/digests/${digestId}`,
    categories: (organizationId: string) => `/api/organizations/${organizationId}/news/categories`,
  },
  note: {
    list: (organizationId: string) => `/api/organizations/${organizationId}/notes`,
    create: (organizationId: string) => `/api/organizations/${organizationId}/notes`,
    item: (organizationId: string, noteId: string) => `/api/organizations/${organizationId}/notes/${noteId}`,
    delete: (organizationId: string, noteId: string) => `/api/organizations/${organizationId}/notes/${noteId}`,
    update: (organizationId: string, noteId: string) => `/api/organizations/${organizationId}/notes/${noteId}`,
  },
  followUp: {
    list: (organizationId: string) => `/api/organizations/${organizationId}/follow_ups`,
    item: (organizationId: string, followUpId: string) =>
      `/api/organizations/${organizationId}/follow_ups/${followUpId}`,
    delete: (organizationId: string, followUpId: string) =>
      `/api/organizations/${organizationId}/follow_ups/${followUpId}`,
    update: (organizationId: string, followUpId: string) =>
      `/api/organizations/${organizationId}/follow_ups/${followUpId}`,
    create: (organizationId: string) => `/api/organizations/${organizationId}/follow_ups`,
  },
  insights: {
    list: (organizationId: string) => `/api/organizations/${organizationId}/insights`,
    item: (organizationId: string, insightId: string) => `/api/organizations/${organizationId}/insights/${insightId}`,
    create: (organizationId: string) => `/api/organizations/${organizationId}/insights`,
    update: (organizationId: string, insightId: string) => `/api/organizations/${organizationId}/insights/${insightId}`,
    delete: (organizationId: string, insightId: string) => `/api/organizations/${organizationId}/insights/${insightId}`,
    archive: (organizationId: string, insightId: string) =>
      `/api/organizations/${organizationId}/insights/${insightId}/archive`,
    activate: (organizationId: string, insightId: string) =>
      `/api/organizations/${organizationId}/insights/${insightId}/activate`,
  },
  topics: {
    list: (organizationId: string) => `/api/organizations/${organizationId}/topics`,
  },
};
